import React from 'react';
import { strings, getCurrentLocale, setUserLocale } from '../../localization';
import globals from '../common/globals';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import HistoryUtil from '../common/historyUtil';
import { ACTIONS as dispatchActions } from 'APP_ROOT/data/layoutTemplateModularFullscreen/duck';
import { ModalContainer } from 'APP_ROOT/components/common/Modal2';
import Language from 'APP_ROOT/components/pages/account/Settings/Language';
import Events from '../common/events';
import SelectDropDown from '../common/SelectDropDown';
import CognitoUtil from '../aws/cognito/cognitoUtil';

const ACTIVE_MENU_ITEM_ELEMENT_ID = "layout-main-navigation-active-item-for-main-module-group-navigation";

export class UserMenu extends React.PureComponent {
    static propTypes = {
        features: PropTypes.object.isRequired,
        user: PropTypes.object,
        accounts: PropTypes.array,
        ravens: PropTypes.array,
        onLogout: PropTypes.func,
        showFeedbackButton: PropTypes.bool,
        onFeedbackButtonClick: PropTypes.func,
        // redux:
        mainNavigationActiveMenuItemId: PropTypes.string,
        setMainNavigationActiveMenuItemId: PropTypes.func.isRequired,
        fullscreenModeIsActive: PropTypes.bool.isRequired
    }

    constructor(props) {
        super(props);
        this.userMenuAccountItemRef = React.createRef();
        this.userMenuAccountDropdownRef = React.createRef(); 
        this.userMenuHelpItemRef = React.createRef();
        this.state = {
            modalContainer: null,
            accountMenuActive: false,
            helpMenuActive: false
        };
    }

    componentDidMount() {
        window.addEventListener('click', this.onWebAppClickEventHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.onWebAppClickEventHandler);
    }

    onWebAppClickEventHandler = (event) => {

        const userMenuAccountItemTarget = this.userMenuAccountItemRef ? this.userMenuAccountItemRef.current : undefined;
        let userMenuAccountItemTargetClicked = false;
        if (userMenuAccountItemTarget) {
            if (event.target === userMenuAccountItemTarget) {
                userMenuAccountItemTargetClicked = true;
            }
            if (event.target.parentElement === userMenuAccountItemTarget) {
                userMenuAccountItemTargetClicked = true;
            }
        }
        
        const userMenuAccountDropdownTarget = this.userMenuAccountDropdownRef ? this.userMenuAccountDropdownRef.current : undefined;
        if (userMenuAccountDropdownTarget && userMenuAccountDropdownTarget.contains(event.target)) {
            userMenuAccountItemTargetClicked = true;
        }

        if (!userMenuAccountItemTargetClicked) {
            this.setState({accountMenuActive: false});
        }

        const userMenuHelpItemTarget = this.userMenuHelpItemRef ? this.userMenuHelpItemRef.current : undefined;
        let userMenuHelpItemTargetClicked = false;
        if (userMenuHelpItemTarget) {
            if (event.target === userMenuHelpItemTarget) {
                userMenuHelpItemTargetClicked = true;
            }
            if (event.target.parentElement === userMenuHelpItemTarget) {
                userMenuHelpItemTargetClicked = true;
            }
        }
        if (!userMenuHelpItemTargetClicked) {
            this.setState({helpMenuActive: false});
        }
    }

    onClickLanguage = () => {

        const dismissModal = () => {
            this.setState({modalContainer: null});
        }

        this.setState({
            modalContainer: (
                <ModalContainer title={strings.settings_language_header} smallDialogMode={true} onClickClose={dismissModal}>
                    <div className="account-page">{/* adding these div wrappers to leverage pre-existing CSS */}
                        <div className="settings-section no-border">
                            <Language stage={this.props.user.stage} hideHeader={true} onSuccess={dismissModal}/>
                        </div>
                    </div>
                </ModalContainer>
            )
        })
    }

    onClickLanguageWithoutUser = () => {

        const dismissModalContainer = () => {
            this.setState({modalContainer: null});
        }

        this.setState({
            modalContainer: (
                <ModalContainer
                    title={strings.settings_language_header}
                    smallDialogMode={true}
                    onClickClose={dismissModalContainer}
                >
                    <div className="message">
                        {strings.settings_language_description}
                    </div>
                    <div className="actions">
                        <button onClick={() => {
                            setUserLocale("en");
                            dismissModalContainer();
                        }} >
                            {strings.locale_name_en_ca}
                        </button>
                        <button onClick={() => {
                            setUserLocale("fr");
                            dismissModalContainer();
                        }} >
                            {strings.locale_name_fr_ca}
                        </button>
                    </div>
                </ModalContainer>
            )
        })
    }

    toggleAccountDropDownMenu = () => {
        this.setState({
            accountMenuActive: !this.state.accountMenuActive
        });
    };

    toggleHelpDropDownMenu = () => {
        this.setState({
            helpMenuActive: !this.state.helpMenuActive
        });
    };

    getOptionsForAccountsDropdown = () => {
        const { accounts, user } = this.props;
        if (!Array.isArray(accounts) || !user) return [];

        // Separate current account
        const currentAccountId = user.currentAccount?.accountId;
        const currentAccount = accounts.find(acc => acc.accountId === currentAccountId);
        const otherAccounts = accounts.filter(acc => acc.accountId !== currentAccountId);

        // Sort other accounts by name
        otherAccounts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        // Return current account first, then sorted others
        return [
            ...(currentAccount ? [{
                value: currentAccount.accountId,
                label: currentAccount.name,
                checked: true,
            }] : []),
            ...otherAccounts.map(acc => ({
                value: acc.accountId,
                label: acc.name,
                checked: false,
            }))
        ];
    }

    onAccountChange = (selectedAccountOption) => {
        if(!selectedAccountOption || !selectedAccountOption.value) {
            console.error("UserMenu onAccountChange: selectedAccountOption is invalid", selectedAccountOption);
            return;
        }

        const selectedAccount = this.props.accounts.find(acc => acc.accountId === selectedAccountOption.value);

        if (!selectedAccount) {
            console.error("UserMenu onAccountChange: selectedAccount not found for option", selectedAccountOption);
            return;
        }

        if(selectedAccount.accountId === this.props.user.currentAccount.accountId) {
            // no change
            return;
        }

        document.dispatchEvent(Events.showLoading({ message: strings.switching_accounts }));

        CognitoUtil.setUserAccountsDetails(selectedAccount, this.props.user.multiAccount);

        setTimeout(() => {
            window.location.href = '/' + getCurrentLocale() + '/dashboard';
        }, 1000);
    }

    onGoToAccountsPage = () => {
        HistoryUtil.push('/select-account');
    }

    renderHelpMenuItems = () => {
        const currentLocale = getCurrentLocale();

        // Items are grouped to control separator placement. A null href means
        // that item is excluded — if the whole group ends up empty, it's dropped too.
        const helpMenuGroups = [
            [
                { href: globals.whatsNewUrl, label: strings.help_menu_whats_new },
                { href: globals.fleetManagersGuideUrl, label: strings.help_menu_fleet_guide },
            ],
            [
                { href: globals.supportUrl, label: strings.help_menu_help_centre },
                { href: globals.installingRavenGuideUrl, label: strings.help_menu_installing_raven },
            ],
            [
                { href: globals.supportEmail ? 'mailto:' + globals.supportEmail : null, label: strings.help_menu_feedback },
                { href: globals.buyRavenUrl, label: strings.help_menu_buy_raven },
            ],
            [
                { href: globals.eulaUrl ? globals.eulaUrl + '#' + currentLocale : null, label: strings.eula },
            ],
        ];

        // Drop groups where every item has a null href
        const nonEmptyGroups = helpMenuGroups.filter(group =>
            group.some(item => !!item.href)
        );

        // For each group, render visible items and add `withSeparator` to the last
        // one — except the last group which needs no separator.
        return (
            <>
                {nonEmptyGroups.map((group, groupIndex) => {
                    const isLastGroup = groupIndex === nonEmptyGroups.length - 1;
                    const visibleItems = group.filter(item => !!item.href);
                    return visibleItems.map((item, itemIndex) => {
                        const isLastItemInGroup = itemIndex === visibleItems.length - 1;
                        const needsSeparator = !isLastGroup && isLastItemInGroup;
                        return (
                            <li key={`help-group-${groupIndex}-item-${itemIndex}`} className={needsSeparator ? 'withSeparator' : undefined}>
                                <a href={item.href} target='_blank'>{item.label}</a>
                            </li>
                        );
                    });
                })}
            </>
        );
    }

    render() {
        const renderWithNavigation = this.props.user && this.props.user.email && this.props.ravens ? true : false;
        const renderLanguageSelection = this.props.features && this.props.features.language_selection ? true : false;
        const renderHelp = this.props.features && this.props.features.show_help ? this.props.features.show_help : false;
        const accountsOptions = this.getOptionsForAccountsDropdown();

        if (!renderWithNavigation && !renderLanguageSelection && !renderHelp) {
            return null; // nothing to render
        }

        let userMenuClass = "layout-user-menu";
        if (this.props.fullscreenModeIsActive) {
            userMenuClass += " fullscreen-mode-is-active";
        }

        const currentLocale = getCurrentLocale();

        // Having a fallback interfers with channels that have set their globals.buyRavenUrl to null in order to hide it from the menu
        // let buyRavenUrl = globals.buyRavenUrl ? globals.buyRavenUrl : "https://ravenconnected.com/pricing";

        // Moved to /data/channels/configurations/prime.js
        // if (this.props.user?.accountChannelCode === "PRIME") {
        //     buyRavenUrl = window.location.origin + "/prime/store";
        // }

        return (
            <>
            <nav className={userMenuClass}>
                <ul>
                    {this.props.showFeedbackButton &&
                        <li>
                            <button onClick={this.props.onFeedbackButtonClick} className='feedback-button'>
                                {strings.feedback}
                            </button>
                        </li>
                    }
                    {renderLanguageSelection ? // language selection
                        <li>
                            <button className="btn-icon-circle-filled" onClick={() => {
                                if (this.props.user && this.props.user.email) {
                                    this.onClickLanguage();
                                } else {
                                    this.onClickLanguageWithoutUser();
                                }
                            }}>
                                {currentLocale.substring(0,2).toUpperCase()}
                            </button>
                        </li>
                    :
                        null
                    }

                    {renderWithNavigation ? // account menu
                        <li>
                            <button ref={this.userMenuAccountItemRef} onClick={this.toggleAccountDropDownMenu}>
                                <i className="material-icons">account_circle</i>
                            </button>
                            {this.state.accountMenuActive ? 
                                <ul>
                                    <li><h4 className='d-inline-block pr-2'>{strings.user}</h4></li>
                                    <li>
                                        {this.props.user.email}
                                    </li>
                                    {this.props.user.multiAccount && accountsOptions.length > 0 && (
                                        <>
                                        <li>
                                            <h5 className='d-inline-block pr-2'>{strings.account}</h5>
                                            <button className='change-account' onClick={this.onGoToAccountsPage} >{strings.user_menu_goto_select_account_page}</button>
                                        </li>
                                        <li ref={this.userMenuAccountDropdownRef} className='account-select-dropdown'>
                                            <SelectDropDown
                                                for="accounts"
                                                options={accountsOptions}
                                                enableSearch={true}
                                                singleOnlySelection={true}
                                                requiredSelection={true}
                                                onChange={index => {
                                                    this.onAccountChange(accountsOptions[index]);
                                                }}
                                            />
                                            
                                        </li>
                                        </>
                                    )}
                                </ul>
                            :
                                null
                            }
                        </li>
                    :
                        null
                    }

                    {renderHelp ? // help menu
                        <li>
                            <button ref={this.userMenuHelpItemRef} onClick={this.toggleHelpDropDownMenu}><i className="material-icons">help</i></button>
                            {this.state.helpMenuActive ? 
                                <ul><h4>{strings.help_menu_title}</h4>
                                    {this.renderHelpMenuItems()}
                                    <li className="copyright">{strings.formatString(strings.footer_copyright, new Date().getFullYear())}</li>
                                </ul>
                            :
                                null
                            }
                        </li>
                    :
                        null
                    }


                    {/* renderWithNavigation ? // settings
                        <li>
                            <Link to={'/' + currentLocale + '/settings'} onClick={() => {
                                // share state with MainNavigation:
                                this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.SETTINGS);
                            }}>
                                <i className="material-icons">settings_applications</i>
                            </Link>
                        </li>
                    :
                        null
                    */}
                </ul>
            </nav>
            {this.state.modalContainer}
            </>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    if (!state.data.layoutTemplateModularFullscreenSharedState) {
        console.error("LayoutTemplateModularFullscreen mapStateToProps layoutTemplateModularFullscreenSharedState reducer property not found");
        return {};
    }

    const { layoutTemplateModularFullscreenSharedState } = state.data;

    return {
        ...layoutTemplateModularFullscreenSharedState // shared between modular fullscreen group and the main and user menus
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        setMainNavigationActiveMenuItemId: (activeMenuItemId) => {
            dispatch(dispatchActions.setMainNavigationActiveMenuItemId(activeMenuItemId));
            return;
        }
    }
}

export { UserMenu as UnconnectedUserMenu }; // for testing
export default connect(mapStateToProps, mapDispatchToProps)(UserMenu);
